import { type APIOptions, importLibrary, setOptions } from "@googlemaps/js-api-loader";

export interface GoogleMapsApi {
  maps: typeof google.maps;
}

type WindowWithGoogle = Window & {
  google?: GoogleMapsApi;
};

export type google = GoogleMapsApi;

type GoogleMapsLibrary = Parameters<typeof importLibrary>[0];

export type LoaderOptions = Omit<APIOptions, "key" | "v" | "libraries"> & {
  version?: APIOptions["v"];
  libraries?: GoogleMapsLibrary[];
  loading?: string;
};

type GoogleMapsLibraryReadinessCheck = (maps: GoogleMapsApi["maps"]) => boolean;
type GoogleMapsLibraryImporter = (library: GoogleMapsLibrary) => Promise<unknown>;

const GOOGLE_MAPS_API_WAIT_TIMEOUT_MS = 10000;
const GOOGLE_MAPS_API_WAIT_INTERVAL_MS = 50;

const GOOGLE_MAPS_LIBRARY_READINESS_CHECKS: Partial<Record<GoogleMapsLibrary, GoogleMapsLibraryReadinessCheck>> = {
  geometry: (maps) => typeof maps.geometry?.spherical?.computeDistanceBetween === "function",
  maps: (maps) => typeof maps.Map === "function" && typeof maps.InfoWindow === "function",
  marker: (maps) => typeof maps.Marker === "function" || typeof maps.marker?.AdvancedMarkerElement === "function",
  places: (maps) => typeof maps.places?.Autocomplete === "function",
};

// A resolved Google Maps namespace does not guarantee every optional library exists.
// Track in-flight work by library so later calls can safely request only the missing pieces.
const googleMapsLibraryLoadPromises = new Map<GoogleMapsLibrary, Promise<unknown>>();
let googleMapsOptionsSignature: string | null = null;
let hasConfiguredLoader = false;

/**
 * Detects Google Maps scripts inserted outside this utility.
 * When one is present, this loader waits for that script instead of configuring a second loader instance.
 */
function documentHasGoogleMapsScript() {
  const scripts = Array.from(document.querySelectorAll("script"));
  return scripts.some((script) => script.src.includes("//maps.googleapis.com"));
}

/**
 * Reads the global Google namespace after another loader or script has initialized it.
 * A partially-created `window.google` without `maps` is treated as unavailable.
 */
function getGoogleMapsApi(): google | null {
  const googleApi = (window as WindowWithGoogle).google;

  return googleApi?.maps ? googleApi : null;
}

/**
 * Confirms that every requested library is usable before consumers receive the API object.
 * This prevents a non-null Google object from escaping while a later library is still missing.
 */
function hasLoadedRequestedLibraries(libraries: GoogleMapsLibrary[], googleApi: google | null): boolean {
  return libraries.every((library) => hasLoadedGoogleMapsLibrary(library, googleApi));
}

/**
 * Checks a single library against the concrete globals that this package depends on.
 * Libraries without a local readiness check are considered ready once the Google namespace exists.
 */
function hasLoadedGoogleMapsLibrary(library: GoogleMapsLibrary, googleApi: google | null): boolean {
  if (!googleApi) {
    return false;
  }

  const readinessCheck = GOOGLE_MAPS_LIBRARY_READINESS_CHECKS[library];

  return readinessCheck ? readinessCheck(googleApi.maps) : true;
}

/**
 * Loads a requested library set while preserving per-library promise reuse.
 * Overlapping calls share the same promise for each library instead of importing duplicates.
 */
function loadGoogleMapsLibraries(
  libraries: GoogleMapsLibrary[],
  importGoogleMapsLibrary: GoogleMapsLibraryImporter
): Promise<unknown[]> {
  return Promise.all(libraries.map((library) => loadGoogleMapsLibrary(library, importGoogleMapsLibrary)));
}

/**
 * Loads one Google Maps library unless it is already ready or already in flight.
 * Failed imports are removed from the cache so a later call can retry the same library.
 */
function loadGoogleMapsLibrary(
  library: GoogleMapsLibrary,
  importGoogleMapsLibrary: GoogleMapsLibraryImporter
): Promise<unknown> {
  const existingGoogle = getGoogleMapsApi();

  if (hasLoadedGoogleMapsLibrary(library, existingGoogle)) {
    return Promise.resolve();
  }

  const existingPromise = googleMapsLibraryLoadPromises.get(library);

  if (existingPromise) {
    return existingPromise;
  }

  const libraryLoadPromise = importGoogleMapsLibrary(library).catch((error) => {
    googleMapsLibraryLoadPromises.delete(library);
    throw error;
  });

  googleMapsLibraryLoadPromises.set(library, libraryLoadPromise);

  return libraryLoadPromise;
}

/**
 * Waits for an externally injected Google Maps script and imports any requested libraries it did not load.
 * Resolves with null on timeout instead of returning a partial API object, and rejects import failures.
 */
function waitForGoogleMapsApi(libraries: GoogleMapsLibrary[]): Promise<google | null> {
  return new Promise((resolve, reject) => {
    const timeoutAt = Date.now() + GOOGLE_MAPS_API_WAIT_TIMEOUT_MS;
    let isChecking = false;

    // Complete the waiter only with a fully-ready API; otherwise report that loading did not satisfy the request.
    const finish = (googleApi: google | null) => {
      window.clearInterval(intervalId);
      resolve(hasLoadedRequestedLibraries(libraries, googleApi) ? googleApi : null);
    };

    // External-script import failures should surface to the caller instead of being swallowed as a timeout.
    const fail = (error: unknown) => {
      window.clearInterval(intervalId);
      reject(error);
    };

    // Poll because external scripts do not expose a shared promise, then use their importLibrary API when available.
    const checkGoogleMapsApi = async () => {
      if (isChecking) {
        return;
      }

      isChecking = true;

      try {
        const googleApi = getGoogleMapsApi();

        if (hasLoadedRequestedLibraries(libraries, googleApi)) {
          finish(googleApi);
          return;
        }

        if (googleApi && typeof googleApi.maps.importLibrary === "function") {
          const importGoogleMapsLibrary = googleApi.maps.importLibrary.bind(
            googleApi.maps
          ) as GoogleMapsLibraryImporter;

          await loadGoogleMapsLibraries(libraries, importGoogleMapsLibrary);

          const loadedGoogleApi = getGoogleMapsApi();

          if (hasLoadedRequestedLibraries(libraries, loadedGoogleApi)) {
            finish(loadedGoogleApi);
            return;
          }
        }

        if (!documentHasGoogleMapsScript() || Date.now() >= timeoutAt) {
          finish(null);
        }
      } catch (error) {
        fail(error);
      } finally {
        isChecking = false;
      }
    };

    const intervalId = window.setInterval(() => {
      checkGoogleMapsApi();
    }, GOOGLE_MAPS_API_WAIT_INTERVAL_MS);

    checkGoogleMapsApi();
  });
}

/**
 * Converts consumer options into the js-api-loader shape and always requests the core `maps` library.
 * Duplicate library names are removed so readiness checks and imports run once per library.
 */
function normalizeLoaderOptions(
  apiKey: string,
  options?: LoaderOptions
): APIOptions & { libraries: GoogleMapsLibrary[] } {
  const { version, libraries, loading: _loading, ...restOptions } = options ?? {};
  const uniqueLibraries = Array.from(new Set<GoogleMapsLibrary>(["maps", ...(libraries ?? [])]));

  return {
    ...restOptions,
    ...(version ? { v: version } : {}),
    key: apiKey,
    libraries: uniqueLibraries,
  };
}

/**
 * Builds the loader configuration signature used for warnings after setOptions has run.
 * Libraries are intentionally excluded because additional libraries can be imported later.
 */
function getLoaderOptionsSignature({
  libraries: _libraries,
  ...options
}: APIOptions & { libraries: GoogleMapsLibrary[] }) {
  return JSON.stringify(options);
}

/**
 * Resets module-level loader state for tests so each case starts without cached configuration or library promises.
 */
export function resetGoogleMapsApiLoaderState() {
  googleMapsLibraryLoadPromises.clear();
  googleMapsOptionsSignature = null;
  hasConfiguredLoader = false;
}

/**
 * Loads Google Maps in the browser and returns it only when all requested libraries are ready.
 * Calls can be sequential or concurrent with different library sets; already-loaded libraries are reused.
 */
export async function safeLoadGoogleMapsApi(apiKey: string, options?: LoaderOptions): Promise<google | null> {
  if (typeof window === "undefined" || !apiKey) {
    return null;
  }

  const loaderOptions = normalizeLoaderOptions(apiKey, options);

  const existingGoogle = getGoogleMapsApi();

  if (hasLoadedRequestedLibraries(loaderOptions.libraries, existingGoogle)) {
    return existingGoogle;
  }

  const nextOptionsSignature = getLoaderOptionsSignature(loaderOptions);

  if (googleMapsOptionsSignature && googleMapsOptionsSignature !== nextOptionsSignature) {
    console.warn("Google Maps API is already configured with different options. Reusing the existing configuration.");
  }

  const hasExistingGoogleMapsScript = documentHasGoogleMapsScript();

  if (hasExistingGoogleMapsScript && !hasConfiguredLoader) {
    console.warn("Google Maps API is already added.");
    await waitForGoogleMapsApi(loaderOptions.libraries);
  } else {
    if (!hasConfiguredLoader) {
      setOptions(loaderOptions);
      hasConfiguredLoader = true;
      googleMapsOptionsSignature = nextOptionsSignature;
    }

    await loadGoogleMapsLibraries(loaderOptions.libraries, importLibrary);
  }

  const loadedGoogle = getGoogleMapsApi();

  // Never hand consumers a Google object until every library they asked for is actually ready.
  return hasLoadedRequestedLibraries(loaderOptions.libraries, loadedGoogle) ? loadedGoogle : null;
}
